import {useCallback} from "react";
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {DecreaseIcon, LockOutlineIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";
import {LISTING_VENDOR_TEST_IDS, PROPERTY_CARD_TEST_ID} from "@pg-mono/test-ids";

import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {AmplitudeFavouriteLocation, AmplitudeFavouritePlacement} from "../../favorite/tracking/get_amplitude_favourites_event_properties";
import {usePrimaryClick} from "../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {OfferType} from "../../offer/types/OfferType";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {getPropertyBadgeType} from "../../property/utils/get_property_badge_type";
import {getPropertyPlanAltText} from "../../property/utils/get_property_plan_modal_title";
import {getAmplitudeItemListContextWithStaticFallback} from "../../tracking/amplitude/get_amplitude_item_list_context_with_static_fallback";
import {AmplitudeItemListName, IAmplitudeItemListContext} from "../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {getCurrency} from "../../utils/get_currency";
import {getThumbnailLink} from "../../utils/get_thumbnail_link";
import {OfferBoxBase} from "../OfferBoxBase";
import {PropertyViewRedirectButton} from "./PropertyViewRedirectButton";

interface IProps {
    onClick?: (property: IPropertyListProperty) => void;
    onLinkClick?: (event: React.MouseEvent<HTMLElement>, property: IPropertyListProperty) => void;
    property: IPropertyListProperty;
    as?: "div";
    activeRedirectButton?: boolean;
    favouritePlacement?: AmplitudeFavouritePlacement;
    favouriteLocation?: AmplitudeFavouriteLocation;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    trackingMeta?: {listId?: string | null; listName?: AmplitudeItemListName | null} | null;
    index?: number;
}

export const PropertyBoxVariantBCD = (props: IProps) => {
    const {
        property,
        onClick,
        onLinkClick,
        as,
        activeRedirectButton,
        favouritePlacement = "rp_favourite_listing_tile",
        favouriteLocation = "listing_page",
        amplitudeItemListContext,
        trackingMeta,
        index
    } = props;
    const {offer} = property;

    const favouriteItemListContext = getAmplitudeItemListContextWithStaticFallback({
        dynamicItemListContext: amplitudeItemListContext,
        trackingMeta
    });

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const externalPlanUrl = property?.external_plan_url;

    const firstPlanPage = head(property.plan_image_pages);
    const planImage = externalPlanUrl || getThumbnailLink(firstPlanPage?.image_url, 375, 250);
    const altText = getPropertyPlanAltText(offer.name, property);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const showPrice = shouldShowPrice && !!property.price;
    const onPropertyBoxClick = useCallback(() => {
        onClick && onClick(property);
    }, [onClick, property]);
    const onPropertyLinkMouseDown = usePrimaryClick((event: React.MouseEvent<HTMLElement>) => {
        onLinkClick?.(event, property);

        if (event.button === 1) {
            onClick?.(property);
        }
    });

    const badgeType = getPropertyBadgeType(property);

    const Component = as || "a";

    return (
        <OfferBoxBase onClick={onPropertyBoxClick} className={propertyBox}>
            <Component
                href={propertyLink}
                className={propertyBoxLink}
                title={offer.name}
                target={target}
                data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
                onMouseDown={onPropertyLinkMouseDown}
            >
                <FavoriteButton
                    offer={offer}
                    property={property}
                    className={subscribeButton}
                    display={{type: "icon"}}
                    dataTestId={LISTING_VENDOR_TEST_IDS.FAVOURITE_ICON}
                    favouritePlacement={favouritePlacement}
                    favouriteLocation={favouriteLocation}
                    favouriteListContext={{
                        itemListId: favouriteItemListContext?.itemListId,
                        itemListName: favouriteItemListContext?.itemListName,
                        listView: "floor_plans",
                        index
                    }}
                />
                <div className={badgeWrapper}>
                    {badgeType === "reservation" && (
                        <Badge
                            variant="label_danger"
                            icon={(iconProps) => <LockOutlineIcon fill={iconProps.fill} className={cx(iconProps.className, badgeIcon)} size="1.6" />}
                        >
                            <Text variant="info_txt_2" as="span" strong>
                                Rezerwacja
                            </Text>
                        </Badge>
                    )}
                    {badgeType === "promotion" && (
                        <Badge
                            variant="label_info"
                            icon={(iconProps) => <PercentIcon fill={iconProps.fill} className={cx(iconProps.className, badgeIcon)} size="1.6" />}
                        >
                            <Text variant="info_txt_2" as="span" strong>
                                Promocja
                            </Text>
                        </Badge>
                    )}
                    {badgeType === "price_decreased" && (
                        <Badge
                            variant="label_success"
                            icon={(iconProps) => <DecreaseIcon fill={iconProps.fill} className={cx(iconProps.className, badgeIcon)} size="1.6" />}
                        >
                            <Text variant="info_txt_2" as="span" strong>
                                Zmiana ceny
                            </Text>
                        </Badge>
                    )}
                </div>

                <div className={planWrap} data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CARD_IMAGE}>
                    <CenteredImage
                        src={planImage}
                        alt={altText}
                        width={375}
                        height={250}
                        breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                        loading="lazy"
                    />
                </div>

                <div className={contentWrap}>
                    <ul className={propertyBoxInfoList}>
                        <li className={propertyBoxInfoListItem}>
                            <Text className={label} variant="info_txt_3" as="span">
                                Metraż
                            </Text>
                            <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.AREA}>
                                {areaFormat(property.area, {precision: 2})}
                            </Text>
                        </li>

                        <li className={propertyBoxInfoListItem}>
                            <Text className={label} variant="info_txt_3" as="span">
                                Pokoje
                            </Text>
                            <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.ROOMS}>
                                {property.rooms}
                            </Text>
                        </li>

                        <li className={propertyBoxInfoListItem}>
                            <Text className={label} variant="info_txt_3" as="span">
                                {offer.type === OfferType.HOUSE ? "Piętra" : "Piętro"}
                            </Text>
                            <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.FLOOR}>
                                {offer.type === OfferType.HOUSE ? property.floors : property.floor === 0 ? "parter" : property.floor}
                            </Text>
                        </li>

                        {showPrice && (
                            <li className={propertyBoxInfoListItem}>
                                <Text className={label} variant="info_txt_3" as="span">
                                    Cena
                                </Text>
                                <Text variant="info_txt_1" as="span" strong>
                                    {priceFormat(property.price as number, {
                                        unit: getCurrency({currency: offer.currency})
                                    })}
                                </Text>
                            </li>
                        )}
                    </ul>
                </div>
                <PropertyViewRedirectButton property={property} isPlansList link={activeRedirectButton && as === "div" ? propertyLink : undefined} />
            </Component>
        </OfferBoxBase>
    );
};

//  Styles
const badgeLayerIndex = 30;

export const propertyBoxLink = css({
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    position: "relative",
    _hover: {
        color: "inherit",
        textDecoration: "none"
    },
    _focus: {
        color: "inherit",
        textDecoration: "none"
    }
});

const propertyBox = css({
    height: "100%",
    p: 2
});

const planWrap = css({
    flex: "0 0 auto",
    "& img": {
        objectFit: "contain",
        width: "100% !important",
        height: "100% !important"
    }
});

const contentWrap = css({
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    mt: 3,
    mb: 2
});

const propertyBoxInfoList = css({
    pl: 0,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center"
});

const propertyBoxInfoListItem = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const label = css({
    color: "var(--colors-gray-700)",
    mb: 0.5
});

const badgeWrapper = css({
    position: "absolute",
    zIndex: badgeLayerIndex,
    display: "flex",
    gap: "1rem"
});

const badgeIcon = css({
    mr: 1
});

const subscribeButton = css({
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: badgeLayerIndex
});
import {useCallback} from "react";
import {css, cx} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {CenteredImage} from "@pg-design/centered-image";
import {DecreaseIcon, LockOutlineIcon, PercentIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {head} from "@pg-mono/nodash";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, priceFormat} from "@pg-mono/string-utils";
import {LISTING_VENDOR_TEST_IDS, PROPERTY_CARD_TEST_ID} from "@pg-mono/test-ids";

import {FavoriteButton} from "../../favorite/components/FavoriteButton";
import {AmplitudeFavouriteLocation, AmplitudeFavouritePlacement} from "../../favorite/tracking/get_amplitude_favourites_event_properties";
import {usePrimaryClick} from "../../hooks/use_primary_click";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {OfferType} from "../../offer/types/OfferType";
import {IPropertyListProperty} from "../../property/types/PropertyListProperty";
import {getPropertyBadgeType} from "../../property/utils/get_property_badge_type";
import {getPropertyPlanAltText} from "../../property/utils/get_property_plan_modal_title";
import {getAmplitudeItemListContextWithStaticFallback} from "../../tracking/amplitude/get_amplitude_item_list_context_with_static_fallback";
import {AmplitudeItemListName, IAmplitudeItemListContext} from "../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {getCurrency} from "../../utils/get_currency";
import {getThumbnailLink} from "../../utils/get_thumbnail_link";
import {OfferBoxBase} from "../OfferBoxBase";
import {PropertyViewRedirectButton} from "./PropertyViewRedirectButton";

interface IProps {
    onClick?: (property: IPropertyListProperty) => void;
    onLinkClick?: (event: React.MouseEvent<HTMLElement>, property: IPropertyListProperty) => void;
    property: IPropertyListProperty;
    as?: "div";
    activeRedirectButton?: boolean;
    favouritePlacement?: AmplitudeFavouritePlacement;
    favouriteLocation?: AmplitudeFavouriteLocation;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
    trackingMeta?: {listId?: string | null; listName?: AmplitudeItemListName | null} | null;
    index?: number;
}

export const PropertyBoxVariantBCD = (props: IProps) => {
    const {
        property,
        onClick,
        onLinkClick,
        as,
        activeRedirectButton,
        favouritePlacement = "rp_favourite_listing_tile",
        favouriteLocation = "listing_page",
        amplitudeItemListContext,
        trackingMeta,
        index
    } = props;
    const {offer} = property;

    const favouriteItemListContext = getAmplitudeItemListContextWithStaticFallback({
        dynamicItemListContext: amplitudeItemListContext,
        trackingMeta
    });

    const propertyLink = rpAppLink.property.detail.base({
        vendorSlug: offer.vendor.slug,
        offerSlug: offer.slug,
        offerId: offer.id,
        propertyId: property.id
    });

    const target = useResponsiveLinkTarget();
    const externalPlanUrl = property?.external_plan_url;

    const firstPlanPage = head(property.plan_image_pages);
    const planImage = externalPlanUrl || getThumbnailLink(firstPlanPage?.image_url, 375, 250);
    const altText = getPropertyPlanAltText(offer.name, property);

    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: offer.configuration.price_visibility,
        vendorPriceVisibility: offer.vendor.configuration.price_visibility,
        vendorId: offer.vendor.id
    });

    const showPrice = shouldShowPrice && !!property.price;
    const onPropertyBoxClick = useCallback(() => {
        onClick && onClick(property);
    }, [onClick, property]);
    const onPropertyLinkMouseDown = usePrimaryClick((event: React.MouseEvent<HTMLElement>) => {
        onLinkClick?.(event, property);

        if (event.button === 1) {
            onClick?.(property);
        }
    });

    const badgeType = getPropertyBadgeType(property);

    const Component = as || "a";

    return (
        <OfferBoxBase onClick={onPropertyBoxClick} className={propertyBox}>
            <Component
                href={propertyLink}
                className={propertyBoxLink}
                title={offer.name}
                target={target}
                data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CONTAINER}
                onMouseDown={onPropertyLinkMouseDown}
            >
                <FavoriteButton
                    offer={offer}
                    property={property}
                    className={subscribeButton}
                    display={{type: "icon"}}
                    dataTestId={LISTING_VENDOR_TEST_IDS.FAVOURITE_ICON}
                    favouritePlacement={favouritePlacement}
                    favouriteLocation={favouriteLocation}
                    favouriteListContext={{
                        itemListId: favouriteItemListContext?.itemListId,
                        itemListName: favouriteItemListContext?.itemListName,
                        listView: "floor_plans",
                        index
                    }}
                />
                <div className={badgeWrapper}>
                    {badgeType === "reservation" && (
                        <Badge
                            variant="label_danger"
                            icon={(iconProps) => <LockOutlineIcon fill={iconProps.fill} className={cx(iconProps.className, badgeIcon)} size="1.6" />}
                        >
                            <Text variant="info_txt_2" as="span" strong>
                                Rezerwacja
                            </Text>
                        </Badge>
                    )}
                    {badgeType === "promotion" && (
                        <Badge
                            variant="label_info"
                            icon={(iconProps) => <PercentIcon fill={iconProps.fill} className={cx(iconProps.className, badgeIcon)} size="1.6" />}
                        >
                            <Text variant="info_txt_2" as="span" strong>
                                Promocja
                            </Text>
                        </Badge>
                    )}
                    {badgeType === "price_decreased" && (
                        <Badge
                            variant="label_success"
                            icon={(iconProps) => <DecreaseIcon fill={iconProps.fill} className={cx(iconProps.className, badgeIcon)} size="1.6" />}
                        >
                            <Text variant="info_txt_2" as="span" strong>
                                Zmiana ceny
                            </Text>
                        </Badge>
                    )}
                </div>

                <div className={planWrap} data-testid={LISTING_VENDOR_TEST_IDS.PROPERTY_CARD_IMAGE}>
                    <CenteredImage
                        src={planImage}
                        alt={altText}
                        width={375}
                        height={250}
                        breakpoints={[{mediaWidth: "0", height: "200px", width: "100%"}]}
                        loading="lazy"
                    />
                </div>

                <div className={contentWrap}>
                    <ul className={propertyBoxInfoList}>
                        <li className={propertyBoxInfoListItem}>
                            <Text className={label} variant="info_txt_3" as="span">
                                Metraż
                            </Text>
                            <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.AREA}>
                                {areaFormat(property.area, {precision: 2})}
                            </Text>
                        </li>

                        <li className={propertyBoxInfoListItem}>
                            <Text className={label} variant="info_txt_3" as="span">
                                Pokoje
                            </Text>
                            <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.ROOMS}>
                                {property.rooms}
                            </Text>
                        </li>

                        <li className={propertyBoxInfoListItem}>
                            <Text className={label} variant="info_txt_3" as="span">
                                {offer.type === OfferType.HOUSE ? "Piętra" : "Piętro"}
                            </Text>
                            <Text variant="info_txt_1" as="span" dataTestId={PROPERTY_CARD_TEST_ID.FLOOR}>
                                {offer.type === OfferType.HOUSE ? property.floors : property.floor === 0 ? "parter" : property.floor}
                            </Text>
                        </li>

                        {showPrice && (
                            <li className={propertyBoxInfoListItem}>
                                <Text className={label} variant="info_txt_3" as="span">
                                    Cena
                                </Text>
                                <Text variant="info_txt_1" as="span" strong>
                                    {priceFormat(property.price as number, {
                                        unit: getCurrency({currency: offer.currency})
                                    })}
                                </Text>
                            </li>
                        )}
                    </ul>
                </div>
                <PropertyViewRedirectButton property={property} isPlansList link={activeRedirectButton && as === "div" ? propertyLink : undefined} />
            </Component>
        </OfferBoxBase>
    );
};

//  Styles
const badgeLayerIndex = 30;

export const propertyBoxLink = css({
    cursor: "pointer",
    display: "flex",
    flexDirection: "column",
    height: "100%",
    position: "relative",
    _hover: {
        color: "inherit",
        textDecoration: "none"
    },
    _focus: {
        color: "inherit",
        textDecoration: "none"
    }
});

const propertyBox = css({
    height: "100%",
    p: 2
});

const planWrap = css({
    flex: "0 0 auto",
    "& img": {
        objectFit: "contain",
        width: "100% !important",
        height: "100% !important"
    }
});

const contentWrap = css({
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
    mt: 3,
    mb: 2
});

const propertyBoxInfoList = css({
    pl: 0,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-evenly",
    alignItems: "center"
});

const propertyBoxInfoListItem = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "center"
});

const label = css({
    color: "var(--colors-gray-700)",
    mb: 0.5
});

const badgeWrapper = css({
    position: "absolute",
    zIndex: badgeLayerIndex,
    display: "flex",
    gap: "1rem"
});

const badgeIcon = css({
    mr: 1
});

const subscribeButton = css({
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: badgeLayerIndex
});
